import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { activityNeutralButtonClass } from 'features/activity/styles/dataTableStyles';
import AccordionFieldBlock from 'features/datasets/components/AccordionFieldBlock';
import 'features/datasets/components/DatasetWorkspace.css';
import { originKind, originLabel } from 'features/datasets/utils/collaboration';
import { evaluationChip, isVerdictValue, stepText, linksOf } from 'features/datasets/utils/units';
import { Transcript, OutputBlock, VerdictBlock } from 'features/datasets/components/TranscriptBlocks';
import Eval3VerdictFeedback from 'features/evals3/components/Eval3VerdictFeedback';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';

/**
 * One Evals v3 verdict as snapshotted onto the row: the eval and version,
 * pass/fail, the path the walk took, the judge's reasoning, the decision
 * answers, the thumbs (the same control as the evals page, so a correction
 * made here is the same correction) and any node labels.
 */
function VerdictCard({ ev, teamId, onFeedback, onOpenUnit }) {
  const chip = evaluationChip(ev);
  const tone = chip.outcome === 'pass' ? 'var(--feedback-success)' : chip.outcome === 'fail' ? 'var(--feedback-error)' : 'var(--text-muted)';
  const fb = ev.feedback ? { id: ev.feedback.id || null, thumbs: ev.feedback.thumbs, corrected_passed: ev.feedback.corrected_passed, reason_text: ev.feedback.reason } : null;
  return (
    <div className="ds-verdict-card" data-testid="verdict-card">
      <div className="ds-verdict-head">
        <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: tone }} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate font-medium text-[color:var(--text-primary)]">{ev.name || 'Eval'}{ev.version ? <span className="ml-1 font-normal text-[color:var(--text-muted)]">v{ev.version}</span> : null}</span>
        <span className="text-[11px] uppercase tracking-[0.04em]" style={{ color: tone }}>{chip.outcome}</span>
        {ev.score != null ? <span className="text-[11px] tabular-nums text-[color:var(--text-muted)]">{Number(ev.score).toFixed(2)}</span> : null}
        {ev.target === 'run' ? <span className="ds-tag-chip" title="On the run">run</span> : null}
        {ev.eval_run_id && teamId ? (
          <Eval3VerdictFeedback teamId={teamId} verdictId={ev.eval_run_id} value={fb} onChange={(saved) => onFeedback?.(ev, saved)} />
        ) : null}
      </div>
      <div className="ds-verdict-body">
        {ev.reason_code ? <div><span className="text-[color:var(--text-muted)]">Reason </span><span className="font-mono text-[11px] text-[color:var(--text-primary)]">{ev.reason_code}</span></div> : null}
        {Array.isArray(ev.steps) && ev.steps.length ? (
          <ol className="ds-verdict-steps" aria-label="Steps">
            {ev.steps.map((s, i) => <li key={`${s.node_id || s.node}-${i}`}>{stepText(s)}</li>)}
          </ol>
        ) : Array.isArray(ev.path) && ev.path.length ? (
          <div className="ds-verdict-path" aria-label="Path">
            {ev.path.map((n, i) => (
              <React.Fragment key={`${n}-${i}`}>
                {i > 0 ? <span className="sep">›</span> : null}
                <span>{n}</span>
              </React.Fragment>
            ))}
          </div>
        ) : null}
        {ev.judge_reasoning ? <p className="whitespace-pre-wrap text-[color:var(--text-secondary)]">{ev.judge_reasoning}</p> : null}
        {ev.decision ? (
          <div>
            <span className="text-[color:var(--text-muted)]">Decision {ev.decision.node ? `(${ev.decision.node}) ` : ''}</span>
            <span className="font-mono text-[11px]">{JSON.stringify(ev.decision.value)}</span>
          </div>
        ) : null}
        {ev.feedback?.thumbs === 'down' ? (
          <div className="rounded border border-[color:var(--border-subtle)] px-2 py-1">
            <span className="text-[color:var(--text-muted)]">Marked wrong: should be {ev.feedback.corrected_passed ? 'Pass' : 'Fail'}{ev.feedback.fix_kind === 'eval' ? ', the eval is missing a check' : ''}. </span>
            {ev.feedback.reason ? <span>{ev.feedback.reason}</span> : null}
          </div>
        ) : null}
        {Array.isArray(ev.node_labels) && ev.node_labels.length ? (
          <div>
            <span className="text-[color:var(--text-muted)]">Steps </span>
            {ev.node_labels.map((l, i) => (
              <span key={i} className="ds-tag-chip mr-1" title={l.reason || undefined}>
                {l.node_id}: {l.agree ? 'right' : `should be ${typeof l.should_have_been === 'string' ? l.should_have_been : Object.values(l.should_have_been || {}).map(String).join(', ')}`}
              </span>
            ))}
          </div>
        ) : null}
        {onOpenUnit ? (
          <div>
            <button type="button" onClick={onOpenUnit} className="text-[11px] font-medium text-[color:var(--trodo-primary)] hover:underline">
              Open {ev.target === 'run' ? 'run' : ev.target || 'unit'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}


function timeAgo(dateStr) {
  if (!dateStr) return '';
  const secs = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60); if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60); if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function TabBtn({ active, children, onClick }) {
  return (
    <button type="button" role="tab" aria-selected={active} onClick={onClick} className={`ds-row-tab ${active ? 'active' : ''}`}>
      {children}
    </button>
  );
}

export default function DatasetRowDrawer({
  datasetId,
  rowIndex,
  rowCount,
  row,
  initialTab = 'fields',
  members = [],
  embedded = true,
  onClose,
  onPrev,
  onNext,
  onRowChange,
  onOpenOrigin,
  onOpenTags,
  onDelete,
  onRefreshEvaluations,
  onEvaluationFeedback,
  evaluations: evaluationsProp = undefined,
  // Label actions on this row: (action, { expected? }) → the page calls the API and reloads.

  readOnly = false,
}) {
  const [tab, setTab] = useState(initialTab);
  const [inputView, setInputView] = useState('yaml');

  const [refreshing, setRefreshing] = useState(false);
  const teamId = getSelectedTeamNumericId();
  const [expectedView, setExpectedView] = useState('yaml');
  const [metaView, setMetaView] = useState('yaml');

  const [inputError, setInputError] = useState(null);
  const [metaError, setMetaError] = useState(null);


  useEffect(() => { setTab(initialTab); }, [initialTab, row?.id]);

  useEffect(() => {
    setInputError(null);
    setMetaError(null);
  }, [row?.id, row?.itemId]);

  const handleInputChange = useCallback((val, err) => {
    if (err) { setInputError(err); return; }
    setInputError(null);
    onRowChange({ input: val });
  }, [onRowChange]);

  const handleExpectedChange = useCallback((val, err) => {
    if (err) return;
    onRowChange({ expected: val });
  }, [onRowChange]);

  const handleMetaChange = useCallback((val, err) => {
    if (err) { setMetaError(err); return; }
    setMetaError(null);
    onRowChange({ metadata: val });
  }, [onRowChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !embedded) onClose?.();
      if (e.key === 'ArrowUp' && e.altKey) onPrev?.();
      if (e.key === 'ArrowDown' && e.altKey) onNext?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext, embedded]);

  if (!row) return null;

  const rowId = row.itemId ? String(row.itemId).slice(0, 8) : `r${rowIndex + 1}`;
  const createdLabel = row.createdAt ? timeAgo(row.createdAt) : null;
  const origin = row.origin;
  const originType = originKind(origin);
  const unit = row.unit || { kind: 'manual' };
  const isUnit = unit.kind && unit.kind !== 'manual';
  const isVerdict = unit.kind === 'verdict';
  const hasTranscript = Array.isArray(row.messages) && row.messages.length > 0;
  const hasVariables = row.input && typeof row.input === 'object' && Object.keys(row.input).length > 0;
  const evaluations = Array.isArray(evaluationsProp) ? evaluationsProp : (Array.isArray(row.evaluations) ? row.evaluations : []);
  const openUnit = isUnit && !row.expired
    ? () => onOpenOrigin?.({ span_id: unit.span_id, run_id: unit.run_id, conversation_id: unit.conversation_id, kind: unit.kind })
    : null;

  const refresh = async () => {
    if (!onRefreshEvaluations || !row.itemId) return;
    setRefreshing(true);
    try { await onRefreshEvaluations(row.itemId); } finally { setRefreshing(false); }
  };

  const exportRow = () => {
    const blob = new Blob([JSON.stringify({
      input: row.query != null && !(row.input && Object.keys(row.input).length) ? row.query : row.input,
      ...(row.messages ? { messages: row.messages } : {}),
      output: row.output ?? null,
      expected: row.expected,
      evaluations,
      metadata: row.metadata,
      ...(isUnit ? { source: { kind: unit.kind, span_id: unit.span_id, run_id: unit.run_id, conversation_id: unit.conversation_id, agent: unit.agent } } : {}),
    }, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `dataset-row-${rowId}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const panel = (
    <aside className={embedded ? 'ds-row-panel' : 'pointer-events-auto relative flex h-full w-[min(85vw,560px)] flex-col overflow-hidden border-l border-[color:var(--border-default)] bg-[color:var(--surface-raised)] shadow-token-lg'}>
      {/* Header */}
      <div className="ds-row-panel-header">
        <div className="flex items-center gap-1">
          <button type="button" onClick={onPrev} disabled={rowIndex <= 0} className="ds-row-action-icon disabled:opacity-30" title="Previous row">
            <ChevronUp size={13} />
          </button>
          <button type="button" onClick={onNext} disabled={rowIndex >= rowCount - 1} className="ds-row-action-icon disabled:opacity-30" title="Next row">
            <ChevronDown size={13} />
          </button>
        </div>
        <div className="min-w-0 flex-1 px-2 text-center">
          <div className="ds-row-panel-title">Dataset row</div>
          <div className="ds-row-panel-meta">
            {/* The scenario's permanent id: it is how two runs are lined up
                against each other, so it is worth being able to copy. */}
            {row.itemId ? (
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(String(row.itemId))}
                title="Row id. Click to copy."
                className="font-mono hover:text-[color:var(--text-primary)]"
              >
                {rowId}
              </button>
            ) : (
              <span title="Gets an id on save">{rowId}</span>
            )}
            {createdLabel ? <span className="ml-2 font-sans">{createdLabel}</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onClose ? (
            <button type="button" onClick={onClose} className="ds-row-action-icon" title="Close"><X size={13} /></button>
          ) : null}
        </div>
      </div>

      {/* Tabs */}
      <div className="ds-row-tabs" role="tablist">
        <TabBtn active={tab === 'fields'} onClick={() => setTab('fields')}>Fields</TabBtn>
        <TabBtn active={tab === 'evaluations'} onClick={() => setTab('evaluations')}>Evaluations{evaluations.length ? ` (${evaluations.length})` : ''}</TabBtn>
      </div>

      <div className="ds-row-actions">
        <div className="flex-1" />
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={exportRow} className={activityNeutralButtonClass}>Download</button>
          {readOnly ? null : <button type="button" onClick={onDelete} className={`${activityNeutralButtonClass} hover:text-danger`}>Archive</button>}
        </div>
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'fields' && (
          <>
            {hasTranscript ? <Transcript messages={row.messages} title={isVerdict ? 'What the eval saw' : 'Input'} /> : null}
            {row.query != null && !hasTranscript ? (
              <div className="border-b border-[color:var(--border-subtle)] px-3 py-2">
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-secondary)]">Input</div>
                <textarea aria-label="Input text" value={row.query} readOnly={readOnly} rows={Math.min(8, Math.max(2, String(row.query).split('\n').length))}
                  onChange={(e) => onRowChange({ query: e.target.value })}
                  className="input w-full text-[12.5px]" />
              </div>
            ) : null}
            {(hasVariables || (!hasTranscript && row.query == null)) && !isVerdict ? (
              <AccordionFieldBlock
                title={hasTranscript || row.query != null ? 'Variables' : 'Input'}
                readOnly={readOnly}
                fieldKind="input"
                value={row.input || {}}
                viewType={inputView}
                onViewTypeChange={setInputView}
                onChange={handleInputChange}
                error={inputError}
                defaultOpen={!hasTranscript}
              />
            ) : null}

            {row.output != null ? (
              <OutputBlock output={row.output} title={isVerdict ? 'The eval said' : 'Output'} />
            ) : isUnit && !isVerdict ? (
              <div className="border-b border-[color:var(--border-subtle)] px-3 py-2 text-[11px] text-[color:var(--text-muted)]">No output recorded.</div>
            ) : null}

            {isVerdict && isVerdictValue(row.expectedRaw) ? (
              <VerdictBlock title="Should have been" value={row.expectedRaw} />
            ) : (
              <AccordionFieldBlock
                title={isVerdict ? 'Should have been' : 'Expected'}
                readOnly={readOnly}
                fieldKind="expected"
                value={row.expected ?? ''}
                viewType={expectedView}
                onViewTypeChange={setExpectedView}
                onChange={handleExpectedChange}
              />
            )}
            <AccordionFieldBlock
              title="Metadata"
              readOnly={readOnly}
              fieldKind="metadata"
              value={row.metadata || {}}
              viewType={metaView}
              onViewTypeChange={setMetaView}
              onChange={handleMetaChange}
              error={metaError}
            />

            {isUnit ? (
              <div className="flex flex-wrap items-center gap-1.5 border-b border-[color:var(--border-subtle)] px-3 py-2 text-[11px]">
                <span className="text-[color:var(--text-muted)]">Links</span>
                {linksOf(unit).map((l) => (row.expired && l.kind !== 'eval' ? (
                  <span key={l.kind} className="text-[color:var(--text-muted)]" title={`${l.title}: gone from retention; the row keeps its copy`}>{l.label} <span className="ds-expired-badge">expired</span></span>
                ) : (
                  <button key={l.kind} type="button" title={l.title} onClick={() => onOpenOrigin?.(l.open)}
                    className="rounded border border-[color:var(--border-default)] px-1.5 py-px font-medium text-[color:var(--text-primary)] hover:bg-surface-hover">{l.label}</button>
                )))}
              </div>
            ) : origin ? (
              <div className="border-b border-[color:var(--border-subtle)] px-3 py-2 text-[11px]">
                <span className="text-[color:var(--text-muted)]">Origin </span>
                {originType === 'trace' ? (
                  <button type="button" onClick={() => onOpenOrigin?.(origin)} className="font-medium text-[color:var(--trodo-primary)] hover:underline">
                    {originLabel(origin)}
                  </button>
                ) : (
                  <span className="font-medium text-[color:var(--text-secondary)]">{originLabel(origin)}</span>
                )}
              </div>
            ) : null}

          </>
        )}

        {tab === 'evaluations' && (
          <div className="flex flex-col py-3">
            <div className="flex items-center gap-2 px-3 pb-2 text-[11px] text-[color:var(--text-muted)]">
              <span className="flex-1">
                {isVerdict ? 'Verdict' : isUnit ? '' : 'No evaluations'}
              </span>
              {isUnit && row.itemId && onRefreshEvaluations ? (
                <button type="button" onClick={refresh} disabled={refreshing} className={`${activityNeutralButtonClass} disabled:opacity-50`} title={isVerdict ? 'Re-read the votes' : 'Re-read the verdicts'}>
                  {refreshing ? 'Refreshing…' : 'Refresh'}
                </button>
              ) : null}
            </div>
            {evaluations.length === 0 ? (
              <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">
                {isVerdict ? 'The row is the verdict.' : isUnit ? 'No verdicts yet.' : 'No evaluations.'}
              </div>
            ) : evaluations.map((ev, i) => (
              <VerdictCard key={ev.eval_run_id || i} ev={ev} teamId={teamId} onOpenUnit={openUnit}
                onFeedback={(e, saved) => onEvaluationFeedback?.(row.itemId, e, saved)} />
            ))}
          </div>
        )}


      </div>
    </aside>
  );

  if (embedded) return panel;

  return (
    <div className="pointer-events-none absolute inset-0 z-[45] flex justify-end" role="region" aria-label="Dataset row detail">
      {panel}
    </div>
  );
}
